import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RefreshCw, Layers, ArrowRight } from 'lucide-react';

/**
 * ConsolidationDensityPanel — CAST-AI-style consolidation density view.
 *
 * Visualises the bin-pack story the optimizer's plan already implies, but which
 * the UI never surfaced:
 *   • current pack density (live efficiency from the realization snapshot)
 *   • node-count density: N nodes ──▶ M nodes (fewer, denser)
 *   • which underutilized nodes get terminated / which denser nodes get created
 *
 * Read-only. SYSTEM-OF-RECORD = the in-cluster engine plan (preview = actual),
 * so this shows the nodes the engine will ACTUALLY consolidate — not the backend
 * recommender's (which mis-targets unmanaged/bootstrap nodes).
 *   GET /node-optimization/rebalance-plan  (current_nodes, optimized_nodes,
 *        nodes_to_remove[], nodes_to_create[], *_count, available, reason)
 *   GET /node-optimization/realization     (snapshots[].efficiency -> live density)
 */
const nodeLabel = (n) => {
  if (n == null) return '';
  if (typeof n === 'string') return n;
  return n.node || n.name || n.node_name || n.instance || n.type || n.instance_type || JSON.stringify(n);
};

// Humanize machine exclusion-reason codes; raw code survives in title= only.
const REASON_LABELS = {
  'do-not-disrupt': 'Protected workload',
  'pool-budget-exhausted': 'Pool budget used up',
  'system-pod-pin': 'Pinned by system pods',
  'not-karpenter-managed': 'Not managed by Karpenter',
  'min-node-age': 'Settling — too new',
  'cooldown': 'Recently changed',
  'not-ready': 'Not ready',
};
const reasonLabel = (code) => REASON_LABELS[code] || String(code).replace(/[-_]/g, ' ');

const ConsolidationDensityPanel = ({ clusterId, exclusions }) => {
  const [plan, setPlan] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    try {
      const [planRes, realRes] = await Promise.all([
        axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-plan`),
        axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/realization`).catch(() => null),
      ]);
      setPlan(planRes.data || null);
      const snaps = realRes?.data?.snapshots || [];
      setEfficiency(snaps.length ? snaps[snaps.length - 1]?.efficiency ?? null : null);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const currentNodes = plan?.current_nodes || 0;
  const optimizedNodes = plan?.optimized_nodes || 0;
  // Engine plan fields; arrays may be empty when the plan is count-based.
  const terminate = Array.isArray(plan?.nodes_to_remove) ? plan.nodes_to_remove.filter(Boolean) : [];
  const create = Array.isArray(plan?.nodes_to_create) ? plan.nodes_to_create.filter(Boolean) : [];
  const removed = plan?.nodes_to_remove_count != null
    ? plan.nodes_to_remove_count
    : Math.max(0, currentNodes - optimizedNodes);
  const createCount = plan?.nodes_to_create_count != null ? plan.nodes_to_create_count : create.length;
  // Bin-pack tightness improvement: same workloads on fewer nodes ⇒ higher density.
  const densityBefore = efficiency != null ? efficiency : null;
  const densityAfter =
    densityBefore != null && optimizedNodes > 0
      ? Math.min(1, densityBefore * (currentNodes / optimizedNodes))
      : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Layers className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          Consolidation density
        </CardTitle>
        <button onClick={fetchAll} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </CardHeader>
      <CardContent>
        {removed === 0 && createCount === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
            No consolidation pending — the cluster is already densely packed.
          </p>
        ) : (
          <>
            {/* node-count density: N ──▶ M (−k nodes) */}
            <div className="flex items-center flex-wrap gap-3">
              <div className="flex items-center gap-2 text-lg font-semibold tabular-nums">
                <span>{currentNodes} nodes</span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <span className="text-emerald-700 dark:text-emerald-400">{optimizedNodes} nodes</span>
              </div>
              {removed > 0 && (
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                  {removed} fewer {removed === 1 ? 'node' : 'nodes'}
                </Badge>
              )}
            </div>

            {/* density bars (only when we have a live efficiency signal) */}
            {densityBefore != null && (
              <div className="mt-4 space-y-2">
                <DensityBar
                  label="Current utilization"
                  hint="Workload usage divided by provisioned capacity"
                  value={densityBefore}
                  tone="bg-slate-300 dark:bg-slate-600"
                />
                {densityAfter != null && (
                  <DensityBar
                    label="After rebalance"
                    hint="Workload usage divided by provisioned capacity"
                    value={densityAfter}
                    tone="bg-emerald-500"
                  />
                )}
              </div>
            )}

            {/* what's being consolidated */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Nodes to remove ({terminate.length})</p>
                {terminate.length === 0 ? (
                  <p className="text-xs text-slate-400">No nodes to remove</p>
                ) : (
                  <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                    {terminate.slice(0, 8).map((n, i) => (
                      <li key={i} className="font-mono truncate" title={nodeLabel(n)}>{nodeLabel(n)}</li>
                    ))}
                    {terminate.length > 8 && <li className="text-slate-400">+{terminate.length - 8} more</li>}
                  </ul>
                )}
                {/* N2.4 red/grey: WHY nodes are excluded from consolidation */}
                {exclusions && Object.keys(exclusions).length > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Excluded from consolidation ({Object.keys(exclusions).length})</p>
                    <ul className="text-xs space-y-0.5">
                      {Object.entries(exclusions).slice(0, 8).map(([node, reason]) => (
                        <li key={node} className="flex items-center gap-1.5 min-w-0">
                          <Badge
                            title={reason}
                            className={
                              reason === 'do-not-disrupt' ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
                              : reason === 'pool-budget-exhausted' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                            }
                          >{reasonLabel(reason)}</Badge>
                          <span className="font-mono text-xs text-slate-600 dark:text-slate-400 truncate" title={node}>{node}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Nodes to add ({createCount})</p>
                {create.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    {createCount > 0
                      ? `${createCount} ${createCount === 1 ? 'node' : 'nodes'} — sizes decided at provisioning time`
                      : 'No nodes to add'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {create.map((n, i) => {
                      const spot = n.spot || n.capacity_type === 'spot' || n.capacityType === 'spot';
                      const type = n.type || n.instance_type || nodeLabel(n);
                      return (
                        <Badge
                          key={i}
                          title={type}
                          className={spot
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}
                        >
                          <span className="font-mono text-xs">{type}</span>&nbsp;{spot ? '(spot)' : '(on-demand)'}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const DensityBar = ({ label, value, tone, hint }) => (
  <div>
    <div className="flex items-center justify-between mb-0.5">
      <span className="text-xs text-slate-500 dark:text-slate-400" title={hint}>{label}</span>
      <span className="text-xs font-medium text-slate-700 dark:text-slate-300 tabular-nums">{(value * 100).toFixed(0)}%</span>
    </div>
    <div className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded overflow-hidden">
      <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.max(2, Math.min(100, value * 100))}%` }} />
    </div>
  </div>
);

export default ConsolidationDensityPanel;
